import { useMemo, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { AuthContext, type AuthContextType } from '@parleyride/auth';
import { useToast } from '@/components/ui/use-toast';
import { useDemo } from '@/contexts/useDemo';

/**
 * The signed-in identity a demo session presents.
 *
 * Rendered INSTEAD of the real AuthProvider — a different component type, not
 * a branch inside one. Workforce hit React #300/#310 returning early inside
 * its auth component, which changed that component's hook count between
 * renders; swapping the type sidesteps that class of bug entirely, and it also
 * tears down `onAuthStateChange`, so a demo session makes no /auth/v1/ request
 * at all. demo-isolation.test.ts asserts exactly that.
 */
export function DemoAuthProvider({ children }: { children: ReactNode }) {
  const { role, person, exitDemo } = useDemo();
  const { toast } = useToast();

  const value = useMemo<AuthContextType>(() => {
    const user = {
      id: person?.id ?? '',
      email: person?.email ?? '',
      aud: 'authenticated',
      role: 'authenticated',
      app_metadata: { provider: 'demo' },
      user_metadata: { first_name: person?.name.split(' ')[0], demo: true },
      created_at: new Date().toISOString(),
    } as unknown as User;

    const session = {
      access_token: 'demo',
      refresh_token: 'demo',
      token_type: 'bearer',
      expires_in: 3600,
      user,
    } as unknown as Session;

    const unavailable = async () => {
      toast({
        title: 'Not available in the demo',
        description: 'This is sample data — sign up to use the real thing.',
      });
    };

    return {
      user,
      session,
      loading: false,
      isDriver: role === 'driver',
      setIsDriver: () => {},
      signUp: unavailable,
      signIn: unavailable,
      signInWithProvider: unavailable,
      signOut: async () => exitDemo(),
    };
  }, [person, role, exitDemo, toast]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
